/**
 * THE colour palette. Every colour that means something - success, warning, danger, info, a status
 * - comes from here; nothing else in the app should spell out a green, yellow, red or blue of its
 * own. (Neutral greys, the navy of the text and the chart colours are not part of this.)
 *
 * Each group has the same strengths, the ones the calendar's status colours are built from:
 *   tag    - the strong colour: text on a light fill, or a solid fill with white text
 *   badge  - the lighter strong colour: stripes, icons, hover of `tag`
 *   card   - pale: outlines and bands
 *   soft   - barely there: backgrounds
 *   active - one step darker than `tag`: the pressed state
 *
 * The theme (themeConfig) builds antd's success / warning / error / info colours - and so every
 * `color="green"` tag, alert and badge - from these; the status colours (ServiceOrderStatus) are
 * these groups too.
 */

/** The brand colour: primary buttons, links, the selected state. */
export const PRIMARY = '#2CBDFF';

/** The deep blue of headings and strong text on the app's own surfaces. */
export const INK = '#1a3353';

/** The pale blue "chrome" of panels and cards: their outline and their two fills. */
export const CHROME = {
  border: '#d4e4f7',
  bg: '#f0f7ff',
  bgLight: '#f8fbff',
};

export const BLUE = {
  tag: '#1565B8',
  badge: '#2A7BCB',
  card: '#D6E9FF',
  soft: '#EDF5FF',
  active: '#0F4C8A',
};

export const GREEN = {
  tag: '#1B7F43',
  badge: '#2A8B47',
  card: '#DAFBCF',
  soft: '#EEFDE9',
  active: '#14652F',
};

export const YELLOW = {
  tag: '#A8730A',
  badge: '#C48A12',
  card: '#FFEFC2',
  soft: '#FFF8E4',
  active: '#8A5F08',
};

export const DANGER = {
  tag: '#D32F2F',
  badge: '#E02424',
  card: '#FFDEDE',
  soft: '#FFF0F0',
  active: '#B71C1C',
};

export const GREY = {
  tag: '#5F6B7A',
  badge: '#6B7587',
  card: '#E6E7EA',
  soft: '#F4F4F6',
  active: '#49525F',
};

/**
 * Publishes the palette as CSS custom properties on :root (--palette-green-tag, --palette-danger-soft,
 * --palette-primary ...), so stylesheets use the same colours without spelling them out. Called once
 * at start-up, before anything renders.
 */
export const applyPaletteCssVariables = () => {
  const root = document.documentElement.style;
  root.setProperty('--palette-primary', PRIMARY);
  root.setProperty('--palette-ink', INK);
  Object.entries(CHROME).forEach(([name, value]) => root.setProperty(`--palette-chrome-${name}`, value));
  const groups = { blue: BLUE, green: GREEN, yellow: YELLOW, danger: DANGER, grey: GREY };
  Object.entries(groups).forEach(([name, group]) => {
    Object.entries(group).forEach(([strength, value]) =>
      root.setProperty(`--palette-${name}-${strength}`, value)
    );
  });
};
